import { Paper, Typography } from '@mui/material';
import { FlightTrackStatus } from '../../store/slices/flightTrackSlice';

interface FlightTrackNoticeProps {
  status: FlightTrackStatus;
  error: string | null;
}

/**
 * Why the map is not drawing a flown track (#581).
 *
 * Every state but `flown` is a sentence, because a missing polyline is
 * indistinguishable from a broken one: a drone that has not flown, a drone this
 * pilot may not see, and a request that failed all render as an empty map, and
 * the whole point of saying which is that a pilot can tell "nothing happened"
 * from "we could not find out".
 *
 * That is the same reasoning as the off-screen and never-reported counts in
 * `DroneMap`, and the reason this is a `role="status"` with `aria-live` rather
 * than a tooltip: the text appears without the pilot having asked, so it has to
 * be announced.
 */
export const FlightTrackNotice: React.FC<FlightTrackNoticeProps> = ({
  status,
  error,
}) => {
  if (status === 'idle' || status === 'flown') {
    return null;
  }

  const message =
    status === 'loading'
      ? 'Loading flight history…'
      : status === 'not-flown'
        ? 'This drone has no recorded flying in the last 24 hours.'
        : status === 'not-visible'
          ? 'This drone’s flight history is not available to you.'
          : (error ?? 'Could not load the flight history.');

  return (
    <Paper
      role="status"
      aria-live="polite"
      elevation={3}
      sx={{ px: 2, py: 1, maxWidth: 260 }}
    >
      <Typography variant="body2">{message}</Typography>
    </Paper>
  );
};
